import { ITelemetryHandler } from '@ms/telemetry-manager/lib/ITelemetryHandler';
import { IManager } from '@ms/telemetry-manager/lib/Manager';

/**
 * Asynchronous decorator for a telemetry handler which loads a target handler asynchronously
 * and attaches it once ready.
 *
 * This may be used to perform asynchronous initialization needed by handler even if it needs to be passed
 * to a consumer synchronously upfront.
 */
export class AsyncTelemetryHandler implements ITelemetryHandler {
  private _handlerLoader: () => Promise<ITelemetryHandler>;

  constructor(handlerLoader: () => Promise<ITelemetryHandler>) {
    this._handlerLoader = handlerLoader;
  }

  public attach(manager: IManager): () => void {
    let dispose: (() => void) | undefined;
    let isDisposed = false;

    this._getHandler().then((handler: ITelemetryHandler) => {
      if (isDisposed) {
        return;
      }

      // Attach the 'real' handler and obtain its disposal callback.
      // When this is attached, the manager will give it the buffer of recent events to process.
      dispose = handler.attach(manager);
    });

    return () => {
      // Signal that if the handler is still loading, it should not be attached.
      isDisposed = true;

      if (dispose) {
        // If the handler was loaded and attached, dispose it.
        dispose();
      }
    };
  }

  private _getHandler(): Promise<ITelemetryHandler> {
    let getHandler = this._handlerLoader();

    this._getHandler = () => getHandler;

    return getHandler;
  }
}
